/**
 * Size system switch (EU / US W / US M) for the size option of the variant picker.
 * Each size label carries its values in `data-size-eu`, `data-size-us-w` and `data-size-us-m`; the switch rewrites the
 * label text. The variant picker replaces its markup when a variant is selected, so the chosen system is kept at module
 * level and re-applied after every change of the picker.
 *
 * Markup hooks: `size-unit-toggle` with `button[data-unit="eu|us-w|us-m"]` and the labels `[data-size-eu]`.
 */
let currentUnit = 'eu';

const labelFor = (element) => {
  const { sizeEu, sizeUsW, sizeUsM } = element.dataset;
  if (currentUnit === 'us-w') return sizeUsW || sizeEu || '';
  if (currentUnit === 'us-m') return sizeUsM || sizeEu || '';
  return sizeEu || '';
};

const applyUnit = () => {
  document.querySelectorAll('[data-size-eu]').forEach((element) => {
    const text = labelFor(element);
    if (text && element.textContent !== text) element.textContent = text;
  });
  document.querySelectorAll('size-unit-toggle [data-unit]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.getAttribute('data-unit') === currentUnit));
  });
};

class SizeUnitToggle extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener(
      'click',
      (event) => {
        const button = /** @type {Element} */ (event.target).closest('[data-unit]');
        if (!button || !this.contains(button)) return;
        currentUnit = button.getAttribute('data-unit') || 'eu';
        applyUnit();
      },
      { signal: this.#abort.signal }
    );

    // Re-apply after the picker updates its markup (a new variant was selected)
    const picker = this.closest('variant-picker');
    if (picker) {
      this.#observer = new MutationObserver(() => applyUnit());
      this.#observer.observe(picker, { childList: true, subtree: true, characterData: true });
    }
    applyUnit();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('size-unit-toggle')) {
  customElements.define('size-unit-toggle', SizeUnitToggle);
}

/*
 * "Find my perfect size": the visitor types the length of their foot and gets the closest size from the size chart
 * (the smallest size whose inner length is at least as long as the foot). The window is a `dialog[data-size-finder]`
 * that sits next to the variant picker; its sizes come from a JSON script (`[data-finder-data]`).
 */
const parseCm = (text) => {
  const numbers = String(text)
    .split('-')
    .map((part) => parseFloat(part))
    .filter((number) => !Number.isNaN(number));
  return numbers.length ? Math.max(...numbers) : NaN;
};

const recommendSize = (dialog) => {
  const result = dialog.querySelector('[data-finder-result]');
  const select = dialog.querySelector('[data-finder-select]');
  if (!result || !select) return;
  select.hidden = true;
  select.removeAttribute('data-size');

  const input = /** @type {HTMLInputElement | null} */ (dialog.querySelector('[data-finder-length]'));
  const unit = /** @type {HTMLSelectElement | null} */ (dialog.querySelector('[data-finder-unit]'));
  const raw = parseFloat(String(input?.value || '').replace(',', '.'));
  if (!(raw > 0)) {
    result.textContent = '';
    return;
  }
  const cm = unit?.value === 'in' ? raw * 2.54 : raw;

  /** @type {Array<{eu: string, usW: string, usM: string, cm: string}>} */
  let sizes = [];
  try {
    sizes = JSON.parse(dialog.querySelector('[data-finder-data]')?.textContent || '[]');
  } catch (_error) {
    sizes = [];
  }
  const match = sizes.find((size) => parseCm(size.cm) >= cm);
  if (!match) {
    const last = sizes[sizes.length - 1];
    result.textContent = last ? `Your foot is longer than our largest size (EU ${last.eu}, ${last.cm} cm).` : '';
    return;
  }
  result.textContent = `Your size: EU ${match.eu} (US Women ${match.usW} · US Men ${match.usM})`;
  select.setAttribute('data-size', match.eu);
  select.textContent = `Select size ${match.eu}`;
  select.hidden = false;
};

document.addEventListener('click', (event) => {
  const target = /** @type {Element} */ (event.target);
  if (target.closest?.('[data-open-size-finder]')) {
    const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog[data-size-finder]'));
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    dialog.querySelector('[data-finder-length]')?.focus();
    return;
  }
  const dialog = /** @type {HTMLDialogElement | null} */ (target.closest?.('dialog[data-size-finder]') || null);
  if (!dialog) return;
  if (target === dialog || target.closest('[data-finder-close]')) {
    dialog.close();
    return;
  }
  const select = target.closest('[data-finder-select]');
  if (select) {
    const size = select.getAttribute('data-size');
    const radio = size ? document.querySelector(`.variant-option--has-unit-toggle input[value="${CSS.escape(size)}"]`) : null;
    if (radio instanceof HTMLElement) radio.click();
    dialog.close();
  }
});

['input', 'change'].forEach((type) => {
  document.addEventListener(type, (event) => {
    const dialog = /** @type {Element} */ (event.target).closest?.('dialog[data-size-finder]');
    if (dialog) recommendSize(dialog);
  });
});
